import { Injectable } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class MenuPermissionService {

  constructor(
    private router: Router
  ) {}

  /**
   * Filter menu items based on user permissions
   */
  filterMenuItems(menuItems: MenuItem[], userPermissions: any): MenuItem[] {
    if (!userPermissions) {
      return [];
    }

    // console.log('filterMenuItems - Input menu items:', menuItems.map(item => item.label));
    // console.log('filterMenuItems - User permissions:', userPermissions);

    const filteredItems = menuItems.filter(item => {
      // Check if main menu item has permission
      const hasMainPermission = this.hasMenuPermission(item, userPermissions);
      // console.log(`filterMenuItems - ${item.label} has permission:`, hasMainPermission);

      if (!hasMainPermission) {
        // console.log(`filterMenuItems - ${item.label} filtered out (no main permission)`);
        return false;
      }

      // If menu has sub-items, filter them too
      if (item.items && item.items.length > 0) {
        // console.log(`filterMenuItems - ${item.label} has ${item.items.length} sub-items, filtering...`);
        item.items = this.filterSubMenuItems(item.items, userPermissions);
        // console.log(`filterMenuItems - ${item.label} after filtering has ${item.items.length} sub-items`);

        // If no sub-items remain, hide the main menu
        if (item.items.length === 0) {
          // console.log(`filterMenuItems - ${item.label} filtered out (no sub-items remain)`);
          return false;
        }
      }

      // console.log(`filterMenuItems - ${item.label} kept in menu`);
      return true;
    });

    // console.log('filterMenuItems - Filtered menu items:', filteredItems.map(item => item.label));
    return filteredItems;
  }

  /**
   * Filter sub-menu items based on permissions
   */
  private filterSubMenuItems(subItems: MenuItem[], userPermissions: any): MenuItem[] {
    // console.log('filterSubMenuItems - Input sub-items:', subItems.map(item => item.label));

    const filteredSubItems = subItems.filter(subItem => {
      const hasPermission = this.hasMenuPermission(subItem, userPermissions);
      // console.log(`filterSubMenuItems - ${subItem.label} has permission:`, hasPermission);
      return hasPermission;
    });

    // console.log('filterSubMenuItems - Filtered sub-items:', filteredSubItems.map(item => item.label));
    return filteredSubItems;
  }

  /**
   * Check if user has permission for a menu item
   */
  private hasMenuPermission(menuItem: MenuItem, userPermissions: any): boolean {
    // If menu has sub-items, check if user has access to ANY sub-item
    if (menuItem.items && menuItem.items.length > 0) {
      // console.log(`hasMenuPermission - ${menuItem.label} has sub-items, checking access...`);
      return this.hasAccessToAnySubItem(menuItem, userPermissions);
    }

    // For single menu items (no sub-items), check direct route permission
    if (!menuItem.routerLink || !menuItem.routerLink[0]) {
      // console.log(`hasMenuPermission - ${menuItem.label} has no route link, allowing`);
      return true; // No route link, allow menu item
    }

    const route = menuItem.routerLink[0];
    const queryParams = menuItem.queryParams;

    // Check if this is a customer template route with query parameters
    if (route === '/settings/templates/list' && queryParams) {
      // console.log(`hasMenuPermission - ${menuItem.label} is template route with query params:`, queryParams);

      if (queryParams['type'] === 'questionnaire') {
        // console.log(`hasMenuPermission - ${menuItem.label} checking Questionnaire permission`);
        return this.checkPermission('Questionnaire.Questionnaire.view', userPermissions);
      } else if (queryParams['type'] === 'contract') {
        // console.log(`hasMenuPermission - ${menuItem.label} checking Contracts permission`);
        return this.checkPermission('Contracts.Contracts.view', userPermissions);
      }
    }

    const routePermissionMap = this.getRoutePermissionMap();
    const requiredPermission = routePermissionMap[route];

    if (!requiredPermission) {
      // console.log(`hasMenuPermission - ${menuItem.label} has no permission mapping, allowing`);
      return true; // No permission required
    }

    // console.log(`hasMenuPermission - ${menuItem.label} checking permission: ${requiredPermission}`);
    return this.checkPermission(requiredPermission, userPermissions);
  }

  /**
   * Check if user has access to any sub-item of a parent menu
   */
  private hasAccessToAnySubItem(menuItem: MenuItem, userPermissions: any): boolean {
    if (!menuItem.items || menuItem.items.length === 0) {
      // console.log(`hasAccessToAnySubItem - ${menuItem.label} has no sub-items, hiding parent`);
      return false; // No sub-items, don't show parent menu
    }

    // console.log(`hasAccessToAnySubItem - ${menuItem.label} checking ${menuItem.items.length} sub-items:`);

    // Check each sub-item and log the result
    let accessibleSubItems = 0;
    menuItem.items.forEach((subItem, index) => {
      const hasPermission = this.hasMenuPermission(subItem, userPermissions);
      // console.log(`  Sub-item ${index + 1}: ${subItem.label} - has permission: ${hasPermission}`);
      if (hasPermission) {
        accessibleSubItems++;
      }
    });

    const hasAccessToAnySubItem = accessibleSubItems > 0;
    // console.log(`hasAccessToAnySubItem - ${menuItem.label} has ${accessibleSubItems} accessible sub-items out of ${menuItem.items.length}, showing parent: ${hasAccessToAnySubItem}`);
    return hasAccessToAnySubItem;
  }

  /**
   * Check if user has specific permission
   */
  private checkPermission(requiredPermission: string, userPermissions: any): boolean {
    // console.log('checkPermission - Required permission:', requiredPermission);
    // console.log('checkPermission - User permissions:', userPermissions);

    const [category, item, action] = requiredPermission.split('.');

    if (!category || !item || !action) {
      // console.log('checkPermission - Invalid permission format');
      return false;
    }

    const categoryPermissions = userPermissions[category];
    if (!categoryPermissions) {
      // console.log('checkPermission - Category not found:', category);
      // Try customer permission check
      return this.checkCustomerPermission(requiredPermission, userPermissions);
    }

    const itemPermissions = categoryPermissions[item];
    if (!itemPermissions) {
      // console.log('checkPermission - Item not found:', item);
      // Try customer permission check
      return this.checkCustomerPermission(requiredPermission, userPermissions);
    }

    // Check regular permission first
    const hasRegularPermission = itemPermissions[action] === true;
    // console.log('checkPermission - Has regular permission:', hasRegularPermission);

    // If no regular permission, check if user is customer with customer permissions
    if (!hasRegularPermission) {
      // console.log('checkPermission - Checking customer permission...');
      return this.checkCustomerPermission(requiredPermission, userPermissions);
    }

    return hasRegularPermission;
  }

  /**
   * Check if user is a customer and has customer-specific permissions
   */
  private checkCustomerPermission(requiredPermission: string, userPermissions: any): boolean {
    // console.log('checkCustomerPermission - Required permission:', requiredPermission);
    // console.log('checkCustomerPermission - User permissions:', userPermissions);

    // Check if user has customer permissions structure
    const hasCustomerPermissions = (userPermissions as any).Dashboard || userPermissions.Questionnaire || userPermissions.Contracts;
    // console.log('checkCustomerPermission - Has customer permissions:', hasCustomerPermissions);

    if (!hasCustomerPermissions) {
      console.log('checkCustomerPermission - No customer permissions found');
      return false;
    }

    // For customers accessing templates, check if they have Questionnaire or Contracts permission
    if (requiredPermission === 'Settings.Templates.view') {
      const hasQuestionnaire = userPermissions.Questionnaire?.Questionnaire?.view === true;
      const hasContracts = userPermissions.Contracts?.Contracts?.view === true;
      // console.log('checkCustomerPermission - Questionnaire permission:', hasQuestionnaire);
      // console.log('checkCustomerPermission - Contracts permission:', hasContracts);
      const result = hasQuestionnaire || hasContracts;
      // console.log('checkCustomerPermission - Final result for templates:', result);
      return result;
    }

    // For customers accessing other settings, check if they have the specific permission
    if (requiredPermission.startsWith('Settings.')) {
      // Check the specific permission directly
      const [category, item, action] = requiredPermission.split('.');
      const categoryPermissions = userPermissions[category];
      if (categoryPermissions && categoryPermissions[item]) {
        const hasPermission = categoryPermissions[item][action] === true;
        // console.log(`checkCustomerPermission - ${requiredPermission} permission:`, hasPermission);
        return hasPermission;
      }
      // console.log(`checkCustomerPermission - ${requiredPermission} not found in user permissions`);
      return false;
    }

    // console.log('checkCustomerPermission - No matching permission found');
    return false;
  }

  /**
   * Map routes to their required permissions
   */
  private getRoutePermissionMap(): { [key: string]: string } {
    return {
      // Customer routes
      '/customer-dashboard/map': 'Dashboard.Dashboard.view',
      '/customer-dashboard/overview': 'Dashboard.Dashboard.view',
      '/settings/templates/list': 'Settings.Templates.view', // For both regular users and customers

      // Manage Trips
      '/manage-trips/calendar/list': 'ManageTrips.Calendar.view',
      '/manage-trips/trip-requests/list': 'ManageTrips.TripRequests.view',
      '/manage-trips/trip-rejects/list': 'ManageTrips.TripRejects.view',
      '/manage-trips/leg-prioritization/list': 'ManageTrips.LegPrioritization.view',

      // Settings
      '/settings/users/list': 'Settings.Users.view',
      '/settings/roles/list': 'Settings.Roles.view',
      '/settings/trucks/list': 'Settings.Trucks.view',
      '/settings/trailers/list': 'Settings.Trailers.view',
      '/settings/drivers/list': 'Settings.Drivers.view',
      '/settings/locations/list': 'Settings.Locations.view',
      '/settings/exceptions/list': 'Settings.Exceptions.view',

      // Setup
      '/setup/services/list': 'Setup.Services.view',
      '/setup/legs/list': 'Setup.Legs.view',
      '/setup/milestones/list': 'Setup.Milestones.view',
      '/setup/actions/list': 'Setup.Actions.view',

      // Customers
      '/customers/leads/list': 'Customers.Leads.view',
      '/settings/customers': 'Customers.ActiveCustomers.view',
      '/customers/inactive-customers/list': 'Customers.InactiveCustomers.view',
      '/customers/rejectedlead/list': 'Customers.RejectedLeads.view',

      // Reports
      '/reports/detail': 'Reports.Report.view',

      // Dashboards
      '/dashboards/dispatcher-dashboard': 'DispatcherDashboard.DispatcherDashboard.view',
      '/dashboards/lc-dashboard': 'LogisticsDashboard.LogisticsDashboard.view'
    };
  }

  /**
   * Get the first accessible route for a menu category
   */
  getFirstAccessibleRoute(menuItems: MenuItem[]): string | null {
    for (const item of menuItems) {
      if (item.routerLink && item.routerLink[0]) {
        return item.routerLink[0];
      }

      if (item.items && item.items.length > 0) {
        const firstSubRoute = this.getFirstAccessibleRoute(item.items);
        if (firstSubRoute) {
          return firstSubRoute;
        }
      }
    }

    return null;
  }

  /**
   * Check if user has access to any item in a menu category
   */
  hasAccessToCategory(categoryName: string, userPermissions: any): boolean {
    const categoryPermissions = userPermissions[categoryName];
    if (!categoryPermissions) {
      return false;
    }

    // Check if user has view permission for any item in the category
    return Object.keys(categoryPermissions).some(itemName => {
      const itemPermissions = categoryPermissions[itemName];
      return itemPermissions && itemPermissions.view === true;
    });
  }

  /**
   * Get the best route to navigate to for a category
   * Returns the first accessible route in the category
   */
  getBestRouteForCategory(categoryName: string, userPermissions: any): string | null {
    const categoryPermissions = userPermissions[categoryName];
    if (!categoryPermissions) {
      return null;
    }

    // Find the first item with view permission
    for (const [itemName, permissions] of Object.entries(categoryPermissions)) {
      if (permissions && (permissions as any).view === true) {
        // Map item name to route
        const routeMap = this.getItemRouteMap();
        return routeMap[`${categoryName}.${itemName}`] || null;
      }
    }

    return null;
  }

  /**
   * Map category.item to routes
   */
  private getItemRouteMap(): { [key: string]: string } {
    return {
      'ManageTrips.Calendar': '/manage-trips/calendar/list',
      'ManageTrips.TripRequests': '/manage-trips/trip-requests/list',
      'ManageTrips.TripRejects': '/manage-trips/trip-rejects/list',
      'ManageTrips.LegPrioritization': '/manage-trips/leg-prioritization/list',

      'Settings.Users': '/settings/users/list',
      'Settings.Roles': '/settings/roles/list',
      'Settings.Trucks': '/settings/trucks/list',
      'Settings.Trailers': '/settings/trailers/list',
      'Settings.Drivers': '/settings/drivers/list',
      'Settings.Locations': '/settings/locations/list',
      'Settings.Exceptions': '/settings/exceptions/list',
      'Settings.Templates': '/settings/templates/list',

      'Setup.Services': '/setup/services/list',
      'Setup.Legs': '/setup/legs/list',
      'Setup.Milestones': '/setup/milestones/list',
      'Setup.Actions': '/setup/actions/list',

      'Customers.Leads': '/customers/leads/list',
      'Customers.ActiveCustomers': '/settings/customers/list',
      'Customers.InactiveCustomers': '/customers/inactive-customers/list',
      'Customers.RejectedLeads': '/customers/rejectedlead/list',

      'Reports.Report': '/reports/detail',

      'DispatcherDashboard.DispatcherDashboard': '/dashboards/dispatcher-dashboard',
      'LogisticsDashboard.LogisticsDashboard': '/dashboards/lc-dashboard'
    };
  }

  /**
   * Check if user has permission for a specific route
   */
  public hasPermissionForRoute(route: string, userPermissions: any): boolean {
    if (!userPermissions) {
      return false;
    }

    const routePermissionMap = this.getRoutePermissionMap();
    const requiredPermission = routePermissionMap[route];

    if (!requiredPermission) {
      return true; // No permission required
    }

    return this.checkPermission(requiredPermission, userPermissions);
  }

  /**
   * Test method to debug permission checking
   */
  public testCustomerPermissions(): void {
    const testPermissions = {
      "Dashboard": {"Dashboard": {"edit": true, "create": true, "view": true, "inactive": true}},
      "Questionnaire": {"Questionnaire": {"edit": true, "create": true, "view": true, "inactive": true}},
      "Contracts": {"Contracts": {"edit": true, "create": true, "view": true, "inactive": true}},
      "Settings": {
        "Users": {"edit": true, "create": true, "view": true, "inactive": true},
        "Roles": {"edit": true, "create": true, "view": true, "inactive": true}
      }
    };

    console.log('=== TESTING CUSTOMER PERMISSIONS ===');

    // Test Dashboard
    const dashboardResult = this.checkPermission('Dashboard.Dashboard.view', testPermissions);
    console.log('Dashboard permission test:', dashboardResult);

    // Test Templates (for Questionnaire/Contracts)
    const templatesResult = this.checkPermission('Settings.Templates.view', testPermissions);
    console.log('Templates permission test:', templatesResult);

    // Test Users
    const usersResult = this.checkPermission('Settings.Users.view', testPermissions);
    console.log('Users permission test:', usersResult);

    // Test Roles
    const rolesResult = this.checkPermission('Settings.Roles.view', testPermissions);
    console.log('Roles permission test:', rolesResult);

    console.log('=== END TEST ===');
  }

  /**
   * Test method for different parent-submenu scenarios
   */
  public testParentSubmenuScenarios(): void {
    console.log('=== TESTING PARENT-SUBMENU SCENARIOS ===');

    // Scenario 1: User has only Trip Requests permission (not Calendar)
    const scenario1Permissions = {
      "ManageTrips": {
        "TripRequests": {"view": true},
        "Calendar": {"view": false},
        "TripRejects": {"view": false}
      }
    };

    console.log('Scenario 1: User has only Trip Requests permission');
    console.log('Expected: Manage Trips should show (has accessible sub-item)');

    // Scenario 2: User has no Manage Trips permissions at all
    const scenario2Permissions = {
      "Settings": {
        "Users": {"view": true}
      }
    };

    console.log('Scenario 2: User has no Manage Trips permissions');
    console.log('Expected: Manage Trips should be hidden (no accessible sub-items)');

    // Scenario 3: User has all Manage Trips permissions
    const scenario3Permissions = {
      "ManageTrips": {
        "Calendar": {"view": true},
        "TripRequests": {"view": true},
        "TripRejects": {"view": true}
      }
    };

    console.log('Scenario 3: User has all Manage Trips permissions');
    console.log('Expected: Manage Trips should show (all sub-items accessible)');

    console.log('=== END PARENT-SUBMENU TEST ===');
  }
}
